import { arrayWithInsertedAt, objectWithout } from '@art-suite/art-core-ts';
import { getUniqueId } from '../commonLib';
import { getTemplateGroup, getTemplateResolutionOption, getTemplateTask } from './ScanTemplateGetters';
import { updateTemplateGroup, updateTemplateResolutionOption, updateTemplateTask } from './ScanTemplatePureFunctionalUpdates';
import {
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplateRoot,
  ScanTemplateTask,
} from './ScanTemplateSchemas';

// **************************************************************
// Scan TEMPLATE LINE ITEM MUTATIONS
// **************************************************************

export const createTemplateLineItem = (
  partial: Partial<ScanTemplateLineItem>,
  now: number = Date.now(),
): ScanTemplateLineItem => ({
  id: getUniqueId(),
  name: partial.name ?? '',
  description: partial.description,
  values: partial.values ?? undefined,
  skuIdOptions: partial.skuIdOptions ?? [],
  createdAt: partial.createdAt ?? now,
  updatedAt: partial.updatedAt ?? now,
});

export const addTemplateLineItem = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
  lineItem: ScanTemplateLineItem,
  index = 0,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const resolutionOption = getTemplateResolutionOption(template, groupId, taskId, resolutionOptionId);
  if (!resolutionOption) return { ...template, updatedAt: now };

  return updateTemplateResolutionOption(
    template,
    groupId,
    taskId,
    resolutionOptionId,
    {
      lineItemTemplates: arrayWithInsertedAt(resolutionOption.lineItemTemplates, index, lineItem),
    },
    now,
  );
};

export const deleteTemplateLineItem = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
  lineItemId: string,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const resolutionOption = getTemplateResolutionOption(template, groupId, taskId, resolutionOptionId);
  if (!resolutionOption) return { ...template, updatedAt: now };

  // Check if the line item exists
  const lineItemExists = resolutionOption.lineItemTemplates?.some(
    li => li.id === lineItemId,
  );

  if (!lineItemExists) {
    // Just update the resolution option's timestamp but don't modify the line items
    return updateTemplateResolutionOption(template, groupId, taskId, resolutionOptionId, { updatedAt: now }, now);
  }

  return updateTemplateResolutionOption(
    template,
    groupId,
    taskId,
    resolutionOptionId,
    {
      lineItemTemplates: resolutionOption.lineItemTemplates?.filter(
        li => li.id !== lineItemId,
      ),
    },
    now,
  );
};

// **************************************************************
// Scan TEMPLATE RESOLUTION OPTION MUTATIONS
// **************************************************************

export const createTemplateResolutionOption = (partial: Partial<ScanTemplateOption>, now: number = Date.now()): ScanTemplateOption => ({
  id: getUniqueId(),
  name: partial.name ?? '',
  description: partial.description,
  values: partial.values ?? undefined,
  lineItemTemplates: partial.lineItemTemplates ?? [],
  createdAt: partial.createdAt ?? now,
  updatedAt: partial.updatedAt ?? now,
});

export const addTemplateResolutionOption = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOption: ScanTemplateOption,
  index = 0,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const task = getTemplateTask(template, groupId, taskId);
  if (!task) return { ...template, updatedAt: now };

  return updateTemplateTask(
    template,
    groupId,
    taskId,
    {
      optionTemplates: arrayWithInsertedAt(task.optionTemplates, index, resolutionOption),
    },
    now,
  );
};

export const deleteTemplateResolutionOption = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const task = getTemplateTask(template, groupId, taskId);
  if (!task) return { ...template, updatedAt: now };

  // Check if the resolution option exists
  const resolutionOptionExists = task.optionTemplates?.some(
    r => r.id === resolutionOptionId,
  );

  if (!resolutionOptionExists) {
    // Just update the task's timestamp but don't modify the resolution options
    return updateTemplateTask(template, groupId, taskId, { updatedAt: now }, now);
  }

  return updateTemplateTask(
    template,
    groupId,
    taskId,
    {
      optionTemplates: task.optionTemplates?.filter(
        r => r.id !== resolutionOptionId,
      ),
    },
    now,
  );
};

// **************************************************************
// Scan TEMPLATE TASK MUTATIONS
// **************************************************************
export const createTemplateTask = (partial: Partial<ScanTemplateTask>, now: number = Date.now()): ScanTemplateTask => ({
  id: getUniqueId(),
  name: partial.name ?? '',
  description: partial.description,
  values: partial.values ?? undefined,
  trade: partial.trade ?? undefined,
  optionTemplates: partial.optionTemplates ?? [],
  createdAt: partial.createdAt ?? now,
  updatedAt: partial.updatedAt ?? now,
});

export const addTemplateTask = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  task: ScanTemplateTask,
  index = 0,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const group = groupId ? getTemplateGroup(template, groupId) : undefined;

  // normalized template
  const updatedTemplate = {
    ...template,
    taskTemplatesById: {
      ...template.taskTemplatesById,
      [task.id]: task,
    },
    updatedAt: now,
  };
  if (group) {
    return updateTemplateGroup(updatedTemplate, groupId!, { taskTemplateIds: [...(group.taskTemplateIds ?? []), task.id] }, now);
  }
  return updatedTemplate;
};

export const deleteTemplateTask = (template: ScanTemplateRoot, groupId: string | undefined, taskId: string, now: number = Date.now()): ScanTemplateRoot => {
  if (template.taskTemplatesById && template.taskTemplatesById[taskId]) {
    return { ...template, taskTemplatesById: objectWithout(template.taskTemplatesById, taskId), updatedAt: now };
  }
  const group = groupId ? getTemplateGroup(template, groupId) : undefined;
  if (!groupId || !group) return { ...template, updatedAt: now };
  if (!group) return { ...template, updatedAt: now };

  // normalized or denormalized template
  return updateTemplateGroup(template, groupId, {
    taskTemplates: group.taskTemplates?.filter(t => t.id !== taskId),
    taskTemplateIds: group.taskTemplateIds?.filter(id => id !== taskId),
  }, now);
};

// **************************************************************
// Scan TEMPLATE GROUP MUTATIONS
// **************************************************************
export const createTemplateGroup = (partial: Partial<ScanTemplateGroup>, now: number = Date.now()): ScanTemplateGroup => ({
  id: getUniqueId(),
  name: partial.name ?? '',
  description: partial.description,
  values: partial.values ?? undefined,
  taskTemplates: partial.taskTemplates ?? [],
  createdAt: partial.createdAt ?? now,
  updatedAt: partial.updatedAt ?? now,
  category: partial.category ?? undefined,
});

export const addTemplateGroup = (template: ScanTemplateRoot, group: ScanTemplateGroup, index = 0, now: number = Date.now()): ScanTemplateRoot => ({
  ...template,
  groupTemplates: arrayWithInsertedAt(template.groupTemplates ?? [], index, group),
  updatedAt: now,
});

export const deleteTemplateGroup = (template: ScanTemplateRoot, groupId: string, now: number = Date.now()): ScanTemplateRoot => ({
  ...template,
  groupTemplates: template.groupTemplates?.filter(g => g.id !== groupId),
  updatedAt: now,
});

// **************************************************************
// Scan TEMPLATE MUTATIONS
// **************************************************************
export const createScanTemplate = (partial: Partial<ScanTemplateRoot> = {}, now: number = Date.now()): ScanTemplateRoot => ({
  id: getUniqueId(),
  name: partial.name ?? '',
  description: partial.description,
  values: partial.values ?? undefined,
  groupTemplates: partial.groupTemplates ?? [],
  createdAt: partial.createdAt ?? now,
  updatedAt: partial.updatedAt ?? now,
  taskTemplatesById: partial.taskTemplatesById ?? undefined,
});

export const createNormalizedScanTemplate = (partial: Partial<ScanTemplateRoot> = {}, now: number = Date.now()): ScanTemplateRoot =>
  createScanTemplate({ ...partial, taskTemplatesById: {} }, now);
